import useTranslator from "../../hooks/useTranslator";
import {
  amlKycChecks,
  haveValue,
  normalizeResolutions,
  ONFIDO_TOOLTIP_PRIORITY,
  getConsiderResult,
  getOnfidoCustomMessage,
  getOnfidoResolutionStatus,
  selectPreferredServiceFile,
  toast,
} from "../../helper/common";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ButtonActionTooltip from "../ErrorModal/ButtonActionTooltip";
import LandLordContext from "../../context/landlord/LandLordContext";
import { navigateToTenantDocument } from "../../Utils/tenantDocumentNavigation";
import useServiceFile from "../../hooks/useServiceFile";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  Icon,
  InfoList,
  Notice,
  TextArea,
} from "../design";
import { ReportSectionTitle, ReportToggle } from "./ReportDrawerParts";
import "./reportDrawer.css";

const TOOLTIP_CLASS = "react-tooltip-styling details-skor-tooltip";

const RESOLUTION_CATEGORY_LABELS = {
  data_consistency: "dataConsistency",
  visual_authenticity: "visualAuthenticity",
  age_validation: "ageValidation",
  image_integrity: "imageIntegrity",
  data_validation: "dataValidation",
  compromised_document: "compromisedDocument",
  sanction: "sanctions",
  politically_exposed_person: "politicallyExposedPerson",
  adverse_media: "adverseMedia",
  legal_and_regulatory_warnings: "legalAndRegulatoryWarnings",
};

const KycAmlCheckReportModal = ({
  isOpen,
  onClose,
  user,
  resolutions,
  onResolutionChange,
  serviceFile = null,
  isGuarantor = false,
}) => {
  const { _t } = useTranslator();
  const { getOnfidoTimelineReport, markProfileResolved } =
    useContext(LandLordContext);
  const kycAmlCheckResult = amlKycChecks(user, resolutions);

  const [searchParams] = useSearchParams();
  const [showDropdown, setShowDropDown] = useState(
    () => searchParams.get("kycAml") === "true"
  );
  const [showResolutionsDropdown, setShowResolutionsDropdown] = useState(false);

  useEffect(() => {
    if (searchParams.get("kycAml") === "true") {
      setShowDropDown(true);
    }
  }, [searchParams]);
  const output =
    user?.onfido_user_kyc?.onfido_workflow_res?.output ??
    user?.onfido_user_kyc?.onfido_workflow_res?.resource?.output;

  const {
    categoryResults,
    resolvedCategoryKeys,
    resolvedByText,
    resolvedByNote,
    allUnclearsResolved,
  } = useMemo(
    () => getOnfidoResolutionStatus(output, resolutions),
    [output, resolutions]
  );

  const resolutionsList = useMemo(() => {
    return normalizeResolutions(resolutions)
      .filter((entry) => haveValue(entry?.meta))
      .flatMap((entry) => {
        const metas = Array.isArray(entry?.meta)
          ? entry.meta
          : [entry?.meta?.key ?? entry?.meta?.categoryKey ?? entry?.meta];
        return metas
          .filter((meta) => haveValue(meta))
          .map((meta) => ({
            meta,
            landlordName: entry?.landlordName,
            note: entry?.note,
          }));
      });
  }, [resolutions]);
  console.log("resolutionsList", resolutionsList);

  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState([]);
  const [resolveNote, setResolveNote] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  const toggleCategory = (key) => {
    setSelectedCategoryKeys((current) =>
      current.includes(key)
        ? current.filter((categoryKey) => categoryKey !== key)
        : [...current, key]
    );
  };

  const renderResolutionControl = (section, key) => {
    const category = categoryResults.statuses.find(
      (status) => status.section === section && status.key === key
    );
    if (!category || category.result === "clear") return null;

    const isResolved = resolvedCategoryKeys.has(key);
    const isSelected = selectedCategoryKeys.includes(key);
    return (
      <Checkbox
        checked={isResolved || isSelected}
        disabled={isResolved}
        onChange={() => toggleCategory(key)}
        ariaLabel={
          isResolved
            ? _t("resolved", "Resolved")
            : _t("selectForResolution", "Select for resolution")
        }
        label={
          isResolved
            ? _t("resolved", "Resolved")
            : isSelected
              ? _t("selected", "Selected")
              : _t("resolve", "Resolve")
        }
      />
    );
  };

  const handleResolveCategories = async () => {
    if (!selectedCategoryKeys.length || !user?._id) return;

    setIsResolving(true);
    try {
      // Merge already-resolved categories with the newly selected ones
      // and send a single aggregated request. Sending parallel per-category
      // requests causes a race (last write wins) and, if the backend
      // replaces the resolution record instead of appending, the previous
      // resolution is lost — see reported bug where resolving a second
      // KYC issue reset the first.
      const nextMetas = [
        ...new Set([...resolvedCategoryKeys, ...selectedCategoryKeys]),
      ];

      await markProfileResolved({
        tenant_id: user._id,
        type: "kyc",
        note: resolveNote.trim(),
        meta: nextMetas.length === 1 ? nextMetas[0] : nextMetas,
      });
      await onResolutionChange?.();
      setSelectedCategoryKeys([]);
      setResolveNote("");
      toast.success(
        _t("kycCategoriesMarkedResolved", "KYC categories marked resolved")
      );
    } catch (error) {
      console.log(error, "Error resolving KYC categories");
      toast.error(_t("failedToMarkResolved", "Failed to mark resolved"));
    } finally {
      setIsResolving(false);
    }
  };

  const mediaLinksRef = useRef();
  const pepLinksRef = useRef();
  const sanctionLinksRef = useRef();
  const warningLinksRef = useRef();
  const adverseMediaLinksRef = useRef();

  const [isOpenMediaLinks, setIsOpenMediaLinks] = useState(false);
  const [isOpenPepLinks, setIsOpenPepLinks] = useState(false);
  const [isOpenSanctionLinks, setIsOpenSanctionLinks] = useState(false);
  const [isOpenWarningLinks, setIsOpenWarningLinks] = useState(false);
  const [isOpenAdverseMediaLinks, setIsOpenAdverseMediaLinks] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const isSanctionsCase =
    output?.document_report?.breakdown?.image_integrity?.breakdown
      ?.supported_document?.properties?.issuing_country_subject_to_sanctions ===
    "consider";

  const isPassedDecision = isSanctionsCase
    ? false
    : kycAmlCheckResult || allUnclearsResolved;

  // clear -> lime, failed -> danger, nothing -> neutral, anything else -> warning
  const getStatusTone = (status) => {
    if (!status) return "neutral";

    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === "clear") return "lime";
    if (normalizedStatus === "failed") return "danger";
    return "warning";
  };

  const getDocumentReportTooltip = (type, breakdown) => {
    if (!breakdown) return "";

    // ── 1. Try priority list first ──────────────────────────────────────────
    const sectionEntries = ONFIDO_TOOLTIP_PRIORITY.filter((entry) =>
      entry.path.startsWith(`${type}.`)
    );

    for (const entry of sectionEntries) {
      const subKey = entry.path.split(".")[1];
      const subBreakdown = breakdown?.breakdown?.[subKey];

      if (subBreakdown?.result === "consider") {
        return entry?.explanation;
      }
    }

    // ── 2. Fallback to existing switch case ─────────────────────────────────
    switch (type) {
      case "visual_authenticity": {
        const b = breakdown?.breakdown;

        if (b?.original_document_present?.properties?.scan === "consider") {
          return "onfidoScanDocumentMessage";
        }
        if (
          b?.original_document_present?.properties?.photo_of_screen ===
          "consider"
        ) {
          return "onfidoPhotoOfScreenMessage";
        }
        if (b?.image_integrity?.properties?.blur === "consider") {
          return "na";
        }
        if (b?.document_present?.result === "consider") {
          return "na";
        }
        break;
      }

      case "data_validation": {
        const b = breakdown?.breakdown;

        if (
          b?.document_expiration?.result === "consider" ||
          b?.expiry_date?.result === "consider"
        ) {
          return "onfidoDocumentExpiryMessage";
        }
        break;
      }

      case "image_integrity": {
        const b = breakdown?.breakdown;

        if (
          b?.image_quality?.properties?.two_documents_uploaded === "consider"
        ) {
          return "onfidoTwoDocumentUploadedMessage";
        }
        if (
          b?.conclusive_document_quality?.properties?.missing_back ===
          "consider"
        ) {
          return "onfidoMissingBackMessage";
        }
        if (b?.image_quality?.properties?.other_photo_issue === "consider") {
          return "onfidoOtherPhotoIssueMessage";
        }
        if (
          b?.conclusive_document_quality?.properties
            ?.abnormal_document_features === "consider"
        ) {
          return "onfidoAbnormalDocumentFeaturesMessage";
        }
        break;
      }

      default:
        return "";
    }
    return "";
  };

  // ── Original media array (article media items from match.media[]) ──────────
  const mediaArray = useMemo(() => {
    const matches = output?.aml_report?.properties?.matches ?? [];
    const result = [];

    for (const match of matches) {
      if (!Array.isArray(match?.media)) continue;

      for (const item of match.media) {
        if (item?.url && item?.snippet && item.title !== "(no title)") {
          result.push({
            url: item.url,
            snippet: item.snippet,
            title: item.title,
          });
          if (result.length === 3) return result;
        }
      }
    }

    return result;
  }, [output]);

  // ── Helper: extract source links filtered by aml_type keyword prefix ───────
  const getSourceLinksByType = (typeKeyword) => {
    const matches = output?.aml_report?.properties?.matches ?? [];
    const result = [];

    for (const match of matches) {
      const sources = match?.sources ?? {};

      for (const [, source] of Object.entries(sources)) {
        const amlTypes = source?.aml_types ?? [];
        const matchesType = amlTypes.some((t) =>
          t.toLowerCase().startsWith(typeKeyword.toLowerCase())
        );

        if (matchesType && source?.url) {
          const alreadyAdded = result.some((r) => r.url === source.url);
          if (!alreadyAdded) {
            result.push({
              url: source.url,
              title: source.name || source.url,
            });
          }
          if (result.length === 3) return result;
        }
      }
    }

    return result;
  };

  const pepLinks = useMemo(() => getSourceLinksByType("pep"), [output]);
  const sanctionLinks = useMemo(
    () => getSourceLinksByType("sanction"),
    [output]
  );
  const warningLinks = useMemo(() => getSourceLinksByType("warning"), [output]);
  const adverseMediaLinks = useMemo(
    () => getSourceLinksByType("adverse-media"),
    [output]
  );

  // ── Scroll helpers ─────────────────────────────────────────────────────────
  const moveToMediaLinks = () => {
    setIsOpenMediaLinks(true);
    setTimeout(() => {
      mediaLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToPepLinks = () => {
    setIsOpenPepLinks(true);
    setTimeout(() => {
      pepLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToSanctionLinks = () => {
    setIsOpenSanctionLinks(true);
    setTimeout(() => {
      sanctionLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToWarningLinks = () => {
    setIsOpenWarningLinks(true);
    setTimeout(() => {
      warningLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToAdverseMediaLinks = () => {
    setIsOpenAdverseMediaLinks(true);
    setTimeout(() => {
      adverseMediaLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const cleanSnippet = (str) => {
    if (!str) return "";

    let cleaned = str.trim();

    // Remove leading : "
    cleaned = cleaned.replace(/^:\s*"/, "");

    // Remove wrapping quotes
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.slice(1, -1);
    }

    // Fix double-escaped unicode (\\u → \u)
    cleaned = cleaned.replace(/\\\\u/g, "\\u");

    // Decode unicode characters
    cleaned = cleaned.replace(/\\u([\dA-Fa-f]{4})/g, (_, code) =>
      String.fromCharCode(parseInt(code, 16))
    );

    return cleaned;
  };

  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch (error) {
      return "";
    }
  };

  const toggleDropdown = () => {
    if (isSanctionsCase) {
      return;
    }
    setShowDropDown(!showDropdown);
  };

  const onfidoMessage = getOnfidoCustomMessage(user, _t);

  // Source file used for this KYC run (TenantServiceFile, service=KYC).
  // Powers the "Verified with <doc> › · uploaded <date>" line.
  // The parent prefetches it on profile load (cached in the hook), so the
  // modal's own call below resolves from cache instead of fetching on open.
  const fetchedKycFile = useServiceFile(user?._id, "KYC", isOpen, isGuarantor);
  // `serviceFile` may be a single record or the raw serviceFiles array — when
  // a DNI was uploaded as front + back, prefer the front document.
  const kycServiceFile = useMemo(
    () => selectPreferredServiceFile(serviceFile ?? fetchedKycFile),
    [serviceFile, fetchedKycFile]
  );
  const verifiedDocType =
    kycServiceFile?.metadata?.document_type ??
    output?.document_report?.properties?.document_type ??
    kycServiceFile?.metadata?.id_type ??
    null;
  // Number from this KYC run's service file only. Same field order as before:
  // personal_number, dni_nie, document_number, passport_id, id_number.
  // Do not fall back to profile identity — that can label a different document
  // than the file this check actually used.
  const verifiedDocNumber =
    [
      kycServiceFile?.metadata?.personal_number,
      kycServiceFile?.metadata?.dni_nie,
      kycServiceFile?.metadata?.document_number,
      kycServiceFile?.metadata?.passport_id,
      kycServiceFile?.metadata?.id_number,
    ].find((value) => haveValue(value)) ?? null;
  const maskedDocNumber = haveValue(verifiedDocNumber)
    ? `···${String(verifiedDocNumber).slice(-4)}`
    : "";
  const verifiedDocTypeLabel = !haveValue(verifiedDocType)
    ? ""
    : verifiedDocType === "dni_nie"
      ? "DNI"
      : _t(String(verifiedDocType));
  const kycFileDate = kycServiceFile?.created_at
    ? moment(kycServiceFile.created_at)
    : null;
  const hasKycFileDate = Boolean(kycFileDate?.isValid());
  const hasKycSourceFile = Boolean(
    kycServiceFile && (haveValue(kycServiceFile.url) || hasKycFileDate)
  );
  const hasKycDocumentDetails = Boolean(
    haveValue(verifiedDocTypeLabel) || haveValue(maskedDocNumber)
  );
  const checkedWithLabel = _t("checkedWith");
  const verifiedWithLabel = _t("verifiedWith");
  const sourceFileLabel = isPassedDecision
    ? verifiedWithLabel
    : checkedWithLabel;
  const uploadedOnLabel = _t("uploadedOn");

  const handleDownloadReport = async () => {
    try {
      setIsDownloading(true);
      const res = await getOnfidoTimelineReport(user?._id);
      if (res?.status === "success" && res?.url) {
        const url = res.url;
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Reusable link section renderer ────────────────────────────────────────
  const renderLinkSection = ({ links, isOpen, setIsOpen, ref, label }) => {
    if (!links.length) return null;

    return (
      <div className="reportDrawer__links" ref={ref}>
        <ReportToggle open={isOpen} onClick={() => setIsOpen(!isOpen)}>
          <Icon name="link" size={16} />
          <span>{label}</span>
          <Badge tone="neutral">{links.length}</Badge>
        </ReportToggle>
        {isOpen && (
          <ul className="reportDrawer__link-list">
            {links.map((link, index) => (
              <li className="reportDrawer__link-row" key={index}>
                {haveValue(link?.url) ? (
                  <>
                    <span className="reportDrawer__link-domain">
                      {index + 1}.
                    </span>
                    <a
                      className="reportDrawer__link"
                      href={link?.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="reportDrawer__link-title">
                        {link?.title}
                      </span>
                      <span className="reportDrawer__link-domain">
                        {getDomain(link?.url)}
                      </span>
                      <Icon name="link" size={14} />
                    </a>
                  </>
                ) : (
                  <span>{_t("urlNotFound")}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const documentBreakdown = output?.document_report?.breakdown;
  const amlBreakdown = output?.aml_report?.breakdown;

  // Status badge + optional "Resolve" tick box for one check row.
  // `tooltipId` (when set) puts the warning tooltip on the badge.
  const renderCheckValue = ({ section, key, result, tooltipId, tooltip }) => (
    <span className="reportDrawer__check">
      {renderResolutionControl(section, key)}
      {tooltip}
      <span
        data-tooltip-id={tooltipId}
        className={
          tooltipId && result === "consider" ? "reportDrawer__clickable" : ""
        }
      >
        <Badge tone={getStatusTone(result)}>{_t(result) || _t("na")}</Badge>
      </span>
    </span>
  );

  // Document checks that explain themselves in a tooltip
  const renderDocumentTooltip = (type) => (
    <ReactTooltip
      id={`${type.replace(/_/g, "-")}-warning`}
      noArrow
      className={TOOLTIP_CLASS}
      render={() => {
        const tooltipData = getDocumentReportTooltip(
          type,
          documentBreakdown?.[type]
        );
        if (!tooltipData) return;
        return <p>{_t(tooltipData)}</p>;
      }}
    />
  );

  const documentCheckRow = (key, withTooltip = false) => {
    const result = documentBreakdown?.[key]?.result;
    return {
      label: _t(
        {
          data_consistency: "dataConsistency",
          visual_authenticity: "visualAuthenticity",
          age_validation: "ageValidation",
          image_integrity: "imageIntegrity",
          data_validation: "dataValidation",
          compromised_document: "compromisedDocument",
        }[key]
      ),
      value: renderCheckValue({
        section: "document_report",
        key,
        result,
        tooltipId: withTooltip
          ? `${key.replace(/_/g, "-")}-warning`
          : undefined,
        tooltip: withTooltip ? renderDocumentTooltip(key) : null,
      }),
    };
  };

  const amlCheckRow = ({ key, label, tipId, messageKey, links, onSeeMore }) => {
    const result = amlBreakdown?.[key]?.result;
    return {
      label,
      value: renderCheckValue({
        section: "aml_report",
        key,
        result,
        tooltipId: result === "consider" ? tipId : undefined,
        tooltip: (
          <ReactTooltip
            clickable
            id={tipId}
            noArrow
            className={TOOLTIP_CLASS}
            render={() => (
              <div>
                <p>{_t(messageKey)}</p>
                {links.length > 0 && (
                  <p
                    className="underline p-0 mt-2 cursor-pointer"
                    onClick={onSeeMore}
                  >
                    {_t("seeAvailableSources")}
                  </p>
                )}
              </div>
            )}
          />
        ),
      }),
    };
  };

  const dateOrNA = (value) =>
    haveValue(value) ? moment(value).format("DD/MM/YYYY") : _t("notAvailable");

  const documentProperties = output?.document_report?.properties;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={_t("kycAndAmlVerificationCheck")}
      closeLabel={_t("close")}
      footer={
        selectedCategoryKeys.length > 0 ? (
          <section className="reportDrawer__tray" aria-live="polite">
            <TextArea
              id="kyc-resolve-note"
              rows={3}
              placeholder={_t("addNote", "Add a note (optional)")}
              aria-label={_t("addNote", "Add a note (optional)")}
              maxLength={500}
              value={resolveNote}
              disabled={isResolving}
              hint={`${resolveNote.length}/500`}
              onChange={(event) => setResolveNote(event.target.value)}
            />
            <div className="reportDrawer__tray-actions">
              <Button
                variant="secondary"
                size="lg"
                disabled={isResolving}
                onClick={() => {
                  setSelectedCategoryKeys([]);
                  setResolveNote("");
                }}
              >
                {_t("cancel")}
              </Button>
              <Button
                size="lg"
                loading={isResolving}
                onClick={handleResolveCategories}
              >
                {isResolving
                  ? `${_t("resolving", "Resolving")}...`
                  : _t("markResolved", "Mark resolved")}
              </Button>
            </div>
          </section>
        ) : null
      }
    >
      <div className="reportDrawer__stack">
        <Notice
          tone={isPassedDecision ? "success" : "danger"}
          title={
            isSanctionsCase
              ? _t("unsupportedDocumentCountry")
              : isPassedDecision
                ? _t("passed")
                : _t("notPassed")
          }
        >
          <div className="reportDrawer__lines">
            <div>
              {isSanctionsCase
                ? _t("sanctionsCaseKycAndAmlCheckWarningMessage")
                : _t(
                    isPassedDecision
                      ? "successDescriptionInsideModelForKycAndAmlAnalysisCollectively"
                      : "failureDescriptionInsideModelForKycAndAmlAnalysisCollectively"
                  )}
            </div>
            {(onfidoMessage?.explanation ||
              onfidoMessage?.recommendedActions) && (
              <>
                <div>
                  <strong>{_t("explanationSummary")}:</strong>{" "}
                  {onfidoMessage?.explanation}
                </div>
                <div>
                  <strong>{_t("recommendedActions")}:</strong>{" "}
                  {onfidoMessage?.recommended_action}
                </div>
              </>
            )}
            {hasKycSourceFile && hasKycDocumentDetails && (
              <div className="reportDrawer__source">
                <Icon name="fileText" size={16} />
                <span>{sourceFileLabel}</span>
                {kycServiceFile ? (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      navigateToTenantDocument({
                        tenantId: user?._id,
                        isGuarantor,
                        service: "KYC",
                        serviceFile: kycServiceFile,
                      });
                      onClose?.();
                    }}
                  >
                    {verifiedDocTypeLabel} {maskedDocNumber} ›
                  </Button>
                ) : (
                  <span>
                    {verifiedDocTypeLabel} {maskedDocNumber}
                  </span>
                )}
                {hasKycFileDate && (
                  <span>
                    · {uploadedOnLabel} {kycFileDate.format("DD-MM-YYYY")}
                  </span>
                )}
              </div>
            )}
            <div className="reportDrawer__action">
              <Button
                variant="outline"
                size="sm"
                loading={isDownloading}
                iconStart={<Icon name="download" size={16} />}
                onClick={handleDownloadReport}
              >
                {isDownloading
                  ? `${_t("downloading")}...`
                  : _t("downloadReport")}
              </Button>
            </div>
          </div>
        </Notice>

        <Card
          as="div"
          className={`reportDrawer__card${isSanctionsCase ? " reportDrawer__card--disabled" : ""}`}
        >
          <ReportToggle
            open={showDropdown}
            onClick={toggleDropdown}
            locked={isSanctionsCase}
            chevronTooltipId={
              isSanctionsCase && !showDropdown
                ? "button-action-tooltip"
                : undefined
            }
          >
            <Icon name="user" size={20} />
            <h3 className="reportDrawer__heading">
              {_t("detailedKycAmlInformation")}
            </h3>
          </ReportToggle>

          {showDropdown && (
            <div className="reportDrawer__body">
              {/* ── Identity Details ── */}
              <section>
                <ReportSectionTitle icon="idCard">
                  {_t("identityDetails")}
                </ReportSectionTitle>
                <InfoList
                  items={[
                    {
                      label: _t("firstName"),
                      value: output?.first_name || _t("notAvailable"),
                    },
                    {
                      label: _t("lastName"),
                      value: output?.last_name || _t("notAvailable"),
                    },
                    {
                      label: _t("dateOfBirth"),
                      value: dateOrNA(output?.date_of_birth),
                    },
                    {
                      label: _t("nationality"),
                      value: _t(output?.nationality) || _t("notAvailable"),
                    },
                    {
                      label: _t("gender"),
                      value: documentProperties?.gender || _t("notAvailable"),
                    },
                    {
                      label: _t("placeOfBirth"),
                      value:
                        documentProperties?.place_of_birth ||
                        _t("notAvailable"),
                    },
                  ]}
                />
              </section>

              {/* ── Document Details ── */}
              <section>
                <ReportSectionTitle icon="fileText">
                  {_t("documentDetails")}
                </ReportSectionTitle>
                <InfoList
                  items={[
                    {
                      label: _t("documentTypeOnfido"),
                      value:
                        _t(documentProperties?.document_type) ||
                        _t("notAvailable"),
                    },
                    {
                      label: _t("documentNumber"),
                      value:
                        documentProperties?.document_number ||
                        _t("notAvailable"),
                    },
                    {
                      label: _t("issuingDate"),
                      value: dateOrNA(documentProperties?.issuing_date),
                    },
                    {
                      label: _t("expiryDate"),
                      value: dateOrNA(documentProperties?.date_of_expiry),
                    },
                    {
                      label: _t("issuingCountry"),
                      value:
                        documentProperties?.issuing_country ||
                        _t("notAvailable"),
                    },
                  ]}
                />
              </section>

              {/* ── Document Checks ── */}
              <section>
                <ReportSectionTitle
                  icon="shieldCheck"
                  tooltipId="document-checks-tooltip"
                >
                  {_t("documentChecks")}
                </ReportSectionTitle>
                <ReactTooltip
                  id="document-checks-tooltip"
                  noArrow
                  className={TOOLTIP_CLASS}
                  render={() => (
                    <p>
                      {_t(
                        "documentChecksDescriptionInsideCollectiveKycAndAmlChecks"
                      )}
                    </p>
                  )}
                />
                <InfoList
                  items={[
                    documentCheckRow("data_consistency"),
                    documentCheckRow("visual_authenticity", true),
                    documentCheckRow("age_validation"),
                    documentCheckRow("image_integrity", true),
                    documentCheckRow("data_validation", true),
                    documentCheckRow("compromised_document"),
                  ]}
                />
              </section>

              {/* ── AML Checks ── */}
              <section>
                <ReportSectionTitle
                  icon="shield"
                  tooltipId="aml-checks-tooltip"
                >
                  {_t("amlChecks")}
                </ReportSectionTitle>
                <ReactTooltip
                  id="aml-checks-tooltip"
                  noArrow
                  className={TOOLTIP_CLASS}
                  render={() => (
                    <p>
                      {_t(
                        "amlChecksDescriptionInsideCollectiveKycAndAmlChecks"
                      )}
                    </p>
                  )}
                />
                <InfoList
                  items={[
                    amlCheckRow({
                      key: "sanction",
                      label: _t("sanctions"),
                      tipId: "sanctions-message-warning",
                      messageKey: "onfidoSanctionsWarningMessage",
                      links: sanctionLinks,
                      onSeeMore: moveToSanctionLinks,
                    }),
                    amlCheckRow({
                      key: "politically_exposed_person",
                      label: _t("politicallyExposedPerson"),
                      tipId: "pep-message-warning",
                      messageKey: "onfidoPEPWarningMessage",
                      links: pepLinks,
                      onSeeMore: moveToPepLinks,
                    }),
                    amlCheckRow({
                      key: "adverse_media",
                      label: _t("adverseMedia"),
                      tipId: "adverse-media-message-warning",
                      messageKey: "onfidoAdverseMediaWarningMessage",
                      links: adverseMediaLinks,
                      onSeeMore: moveToAdverseMediaLinks,
                    }),
                    amlCheckRow({
                      key: "legal_and_regulatory_warnings",
                      label: _t("legalAndRegulatoryWarnings"),
                      tipId: "legal-regulatory-message-warning",
                      messageKey: "onfidoLegalAndRegulatoryWarningMessage",
                      links: warningLinks,
                      onSeeMore: moveToWarningLinks,
                    }),
                  ]}
                />

                {/* ── Source link sections ── */}
                {renderLinkSection({
                  links: pepLinks,
                  isOpen: isOpenPepLinks,
                  setIsOpen: setIsOpenPepLinks,
                  ref: pepLinksRef,
                  label: _t("pepLinks"),
                })}
                {renderLinkSection({
                  links: sanctionLinks,
                  isOpen: isOpenSanctionLinks,
                  setIsOpen: setIsOpenSanctionLinks,
                  ref: sanctionLinksRef,
                  label: _t("sanctionLinks"),
                })}
                {renderLinkSection({
                  links: warningLinks,
                  isOpen: isOpenWarningLinks,
                  setIsOpen: setIsOpenWarningLinks,
                  ref: warningLinksRef,
                  label: _t("legalWarningLinks"),
                })}
                {renderLinkSection({
                  links: adverseMediaLinks,
                  isOpen: isOpenAdverseMediaLinks,
                  setIsOpen: setIsOpenAdverseMediaLinks,
                  ref: adverseMediaLinksRef,
                  label: _t("adverseMediaLinks"),
                })}
                {renderLinkSection({
                  links: mediaArray,
                  isOpen: isOpenMediaLinks,
                  setIsOpen: setIsOpenMediaLinks,
                  ref: mediaLinksRef,
                  label: _t("mediaLinks"),
                })}
              </section>
            </div>
          )}
        </Card>

        {resolutionsList.length > 0 && (
          <Card as="div" className="reportDrawer__card">
            <ReportToggle
              open={showResolutionsDropdown}
              onClick={() =>
                setShowResolutionsDropdown(!showResolutionsDropdown)
              }
            >
              <Icon name="shieldCheck" size={20} />
              <h3 className="reportDrawer__heading">
                {_t("resolutions", "Resolutions")}
              </h3>
            </ReportToggle>

            {showResolutionsDropdown && (
              <div className="reportDrawer__body">
                <div className="reportDrawer__resolutions">
                  {resolutionsList.map((resolution, index) => (
                    <div
                      className="reportDrawer__resolution"
                      key={`${resolution.meta}-${index}`}
                    >
                      <strong>
                        {_t(
                          RESOLUTION_CATEGORY_LABELS[resolution.meta] ??
                            resolution.meta,
                          resolution.meta
                        )}
                      </strong>
                      <span>
                        {_t("resolvedByLandlord", "Resolved by")}{" "}
                        <strong>{resolution.landlordName}</strong>
                      </span>
                      {resolution.note && (
                        <span>
                          <strong>{_t("note", "Note")}:</strong>{" "}
                          {resolution.note}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
      <ButtonActionTooltip />
    </Drawer>
  );
};

export default KycAmlCheckReportModal;
